import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { getBillingStatus, acknowledgeBillingInfo } from "@/api/client";
import { T, useT } from "@/lib/translation";
import { useLocale } from "@/hooks/useLocale";

/**
 * Unskippable explainer for trial/pricing/referral mechanics. Shown to anyone —
 * existing user or brand new signup — who hasn't acknowledged it yet
 * (BillingStatus.billing_info_acknowledged). No close button, outside-click,
 * or Escape dismissal: the only way out is the in-content confirm button.
 * Never shown to friends & family (is_ff) — billing doesn't apply to them.
 */
export default function BillingInfoModal() {
  const queryClient = useQueryClient();
  const t = useT();
  const { intlLocale } = useLocale();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["billing-info-status"],
    queryFn: getBillingStatus,
  });

  // Also suppressed once the user already has a real subscription: this modal
  // explains pre-payment trial/pricing mechanics, which no longer apply once
  // they've actually subscribed. Matters for users who were blocked the whole
  // time (never got a chance to see/acknowledge it) and only cleared the
  // block by paying directly — without this, they'd see stale "here's how
  // your trial works" copy immediately after already paying.
  const hasSubscription =
    data?.subscription_status === "active" ||
    data?.subscription_status === "trialing";
  const open =
    !isLoading &&
    data != null &&
    !data.is_ff &&
    !data.billing_info_acknowledged &&
    !hasSubscription;

  const handleConfirm = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      await acknowledgeBillingInfo();
      await queryClient
        .invalidateQueries({ queryKey: ["billing-info-status"] })
        .catch(() => {});
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !data) return null;

  const trialEndDate = new Date(data.trial_ends_at).toLocaleDateString(
    intlLocale("en-GB"),
    { day: "numeric", month: "long", year: "numeric" },
  );

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        hideClose
        className="sm:max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl">
            {data.is_founding_member
              ? t("billing:infoModal.foundingTitle")
              : t("billing:infoModal.trialTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {data.is_founding_member ? (
            <div className="rounded-lg border bg-muted/40 px-4 py-4 space-y-2 text-sm text-muted-foreground">
              <p>
                <T
                  k="billing:infoModal.founding.until"
                  components={{
                    strong: <strong className="text-foreground" />,
                  }}
                />
              </p>
              <p>
                <T
                  k="billing:infoModal.founding.start"
                  components={{
                    strong: <strong className="text-foreground" />,
                  }}
                />
              </p>
              <p>
                <T
                  k="billing:infoModal.founding.end"
                  components={{
                    strong: <strong className="text-foreground" />,
                  }}
                />
              </p>
            </div>
          ) : (
            <div className="rounded-lg border bg-muted/40 px-4 py-4 space-y-2 text-sm text-muted-foreground">
              <p>
                <T
                  k={
                    data.free_tier_enabled
                      ? "billing:infoModal.trialIntroEverything"
                      : "billing:infoModal.trialIntro"
                  }
                  components={{
                    strong: <strong className="text-foreground" />,
                  }}
                  values={{ date: trialEndDate }}
                />
              </p>
              {data.free_tier_enabled ? (
                <p>{t("billing:infoModal.afterFreeTier")}</p>
              ) : (
                <p>{t("billing:infoModal.afterNoFreeTier")}</p>
              )}
            </div>
          )}

          <div className="rounded-lg border bg-primary/5 border-primary/25 px-4 py-4 space-y-1.5 text-sm">
            <p className="font-medium">
              {t("billing:infoModal.referralTitle")}
            </p>
            <p className="text-muted-foreground">
              <T
                k="billing:infoModal.referralBody"
                components={{ strong: <strong className="text-foreground" /> }}
              />
            </p>
          </div>

          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => setChecked(v === true)}
              className="mt-0.5"
            />
            <span>{t("billing:infoModal.understand")}</span>
          </label>

          <Button
            className="w-full"
            disabled={!checked || submitting}
            onClick={handleConfirm}
          >
            {t("billing:infoModal.continue")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
